// src/v2/hooks/useEmployeeContributionLedger.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useEmployeeContributionLedger = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Internal PDF Generation Logic
   */
  const downloadPDF = useCallback(async (data, employeeName) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      let currentY = 742;

      // Header
      page.drawText("PHILHEALTH CONTRIBUTION LEDGER", { x: 50, y: currentY, size: 16, font: fontBold });
      currentY -= 25;
      page.drawText(`Employee: ${employeeName}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 30;

      // Table Headers
      page.drawRectangle({ x: 50, y: currentY - 20, width: 512, height: 20, color: rgb(0.9, 0.9, 0.9) });
      const cols = { period: 60, ee: 180, er: 280, total: 380 };
      page.drawText("Period", { x: cols.period, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("Employee Share", { x: cols.ee, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("Employer Share", { x: cols.er, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("Total Amount", { x: cols.total, y: currentY - 14, size: 9, font: fontBold });

      currentY -= 20;

      // Rows
      (data || []).forEach((row, index) => {
        currentY -= 20;
        if (index % 2 === 1) page.drawRectangle({ x: 50, y: currentY, width: 512, height: 20, color: rgb(0.97, 0.97, 0.97) });
        
        page.drawText(row.periodName || "-", { x: cols.period, y: currentY + 6, size: 9, font });
        page.drawText((row.phEE || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.ee, y: currentY + 6, size: 9, font });
        page.drawText((row.phER || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.er, y: currentY + 6, size: 9, font });
        page.drawText(((row.phEE || 0) + (row.phER || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.total, y: currentY + 6, size: 9, font });
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PhilHealth_Ledger_${employeeName.replace(/\s+/g, '_')}.pdf`;
      link.click();

      showGeneralMessage("Ledger downloaded successfully.", true);
    } catch (err) {
      showGeneralMessage("Failed to generate PDF.", false);
    }
  }, [showGeneralMessage]);

  /**
   * Orchestrator: Fetch + Download
   */
  const generateAndDownload = useCallback(async (params, employeeName) => {
    setLoading(true);
    try {
      let data = [];
      try {
        data = await reportsService.getReportData('ContributionLedger', params);
      } catch (apiErr) {
        console.warn("Backend not found. Demo fallback: empty list.");
        data = []; 
      }
      
      await downloadPDF(data || [], employeeName);
    } catch (err) {
      showGeneralMessage("Process failed.", false);
    } finally {
      setLoading(false);
    }
  }, [downloadPDF, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};